/**
 * AgentBootstrapManager — política de enrolamiento, tokens y auditoría del agente.
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Cinco bloques (`abm-section`) con **su propio CSS de 379 líneas** (`abm-*`):
 * otra vez inputs, botones, píldoras de estado y tres tablas propias. Y con dos
 * problemas de fondo, además del color:
 *  · **La tabla de credenciales individuales estaba duplicada**: era la misma
 *    información que la pestaña Credenciales, con sus propios botones de revocar
 *    y borrar y su propio diálogo de confirmación. Dos sitios donde hacer lo
 *    mismo, y el usuario sin saber cuál es el bueno.
 *  · Los secretos recién emitidos (token o credencial) se pintaban **abajo del
 *    todo**, después de las tablas: se generaba un token y el valor aparecía
 *    fuera de la vista.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Sección del estándar (cabecera de sección, `Panel`es y tokens de lista):
 *  · **Política** — modo de rollout, TTL y usos, con los tres indicadores
 *    (API key legacy / bootstrap / credencial) como chips que se leen de un golpe.
 *  · **Tokens de bootstrap** — alta y lista en **filas de tokens**, no en tabla.
 *  · **Credenciales individuales** — se queda solo la **reemisión** (que es una
 *    acción de bootstrap) y un aviso claro de que el inventario de credenciales
 *    vive en su pestaña, con enlace.
 *  · **Auditoría** — filas de tokens, con el aviso de cuántos eventos se ven.
 *  · El secreto se entrega en un **diálogo** en cuanto se genera: es un dato que
 *    se enseña una sola vez y no puede quedar al final de la página.
 *
 * Su CSS (`AgentBootstrapManager.css`) se ha borrado: las clases `abm-*` ya no
 * existen en ningún componente.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RocketLaunchIcon,
  KeyIcon,
  PlusIcon,
  NoSymbolIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { Panel } from './common/Panel';
import Dialog from './common/Dialog';
import { hace } from '../utils/relativeTime';
import api from '../services/api';

const MODOS = [
  { value: 'legacy', label: 'legacy · solo API key estática' },
  { value: 'dual', label: 'dual · convivencia completa' },
  { value: 'credential_only', label: 'credential_only · sin API key legacy' },
];

const EVENTOS = {
  bootstrap_exchanged: { label: 'Bootstrap usado', tono: 'list-badge--accent' },
  bootstrap_token_created: { label: 'Token creado', tono: 'list-badge' },
  bootstrap_token_revoked: { label: 'Token revocado', tono: 'list-badge--muted' },
  bootstrap_token_purged: { label: 'Token borrado', tono: 'list-badge--off' },
  credential_reissued: { label: 'Credencial reemitida', tono: 'list-badge--warn' },
  credential_revoked: { label: 'Credencial revocada', tono: 'list-badge--muted' },
  credential_purged: { label: 'Credencial borrada', tono: 'list-badge--off' },
};

/** Fila de un token o de un evento: una línea, con sus datos y sus botones. */
function FilaToken({ children, actions }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '8px 2px', borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
      {actions && <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>{actions}</div>}
    </div>
  );
}

function Avanzar({ hay, children, tono }) {
  if (!hay) return null;
  return (
    <div
      className={`list-badge ${tono}`}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginTop: 10, fontSize: '0.78rem' }}
    >
      <ExclamationTriangleIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}

export default function AgentBootstrapManager({ companyId, onGoToCredentials }) {
  const [policy, setPolicy] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const [rolloutMode, setRolloutMode] = useState('legacy');
  const [expiresInHours, setExpiresInHours] = useState(24);
  const [maxUses, setMaxUses] = useState(1);
  const [newTokenName, setNewTokenName] = useState('Bootstrap token');
  const [tokenNotes, setTokenNotes] = useState('');
  const [agentId, setAgentId] = useState('');
  const [reissueReason, setReissueReason] = useState('Rotación manual');

  const [savingPolicy, setSavingPolicy] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [pending, setPending] = useState('');
  const [secreto, setSecreto] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [policyRes, tokensRes, credentialsRes, eventsRes] = await Promise.all([
        api.getCompanyAgentPolicy(companyId),
        api.getAgentBootstrapTokens(companyId),
        api.getAgentCredentials(companyId),
        api.getAgentCredentialEvents(companyId),
      ]);
      const p = policyRes.policy || null;
      setPolicy(p);
      setRolloutMode(p?.rollout_mode || 'legacy');
      setExpiresInHours(p?.bootstrap_token_ttl_hours || 24);
      setMaxUses(p?.bootstrap_token_max_uses || 1);
      setTokens(tokensRes.tokens || []);
      setCredentials(credentialsRes.credentials || []);
      setEvents(eventsRes.events || []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar la configuración del agente');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 3000);
  };

  const bootstrapHabilitado = !!policy?.bootstrap_enabled;
  const legacyHabilitado = !!policy?.legacy_api_keys_enabled;
  const credencialesHabilitadas = !!policy?.agent_credentials_enabled;
  const activas = useMemo(() => credentials.filter((c) => c.is_active).length, [credentials]);
  const tokensActivos = useMemo(() => tokens.filter((t) => t.is_active).length, [tokens]);

  const guardarPolitica = async () => {
    setSavingPolicy(true);
    setError('');
    try {
      const res = await api.updateCompanyAgentPolicy(companyId, {
        rollout_mode: rolloutMode,
        bootstrap_token_ttl_hours: Number(expiresInHours) || 24,
        bootstrap_token_max_uses: Number(maxUses) || 1,
      });
      setPolicy(res.policy || null);
      mostrarAviso('Política actualizada');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo guardar la política del agente');
    } finally {
      setSavingPolicy(false);
    }
  };

  const crearToken = async () => {
    if (!bootstrapHabilitado) return;
    setCreatingToken(true);
    setError('');
    try {
      const res = await api.createAgentBootstrapToken(companyId, {
        name: newTokenName.trim() || 'Bootstrap token',
        expires_in_hours: Number(expiresInHours) || 24,
        max_uses: Number(maxUses) || 1,
        notes: tokenNotes.trim() || null,
      });
      setSecreto({
        valor: res.token,
        titulo: 'Bootstrap token generado',
        ayuda: 'Cópialo ahora: es el valor que viaja al instalador o al comando bootstrap, y no se vuelve a mostrar.',
      });
      setTokenNotes('');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo crear el bootstrap token');
    } finally {
      setCreatingToken(false);
    }
  };

  const revocarToken = async (id) => {
    setPending(`token-revoke-${id}`);
    try {
      await api.revokeAgentBootstrapToken(companyId, id);
      mostrarAviso('Bootstrap token revocado');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo revocar el bootstrap token');
    } finally {
      setPending('');
    }
  };

  const borrarToken = async (id) => {
    setPending(`token-delete-${id}`);
    try {
      await api.deleteAgentBootstrapToken(companyId, id, 'Purga manual de bootstrap token revocado');
      mostrarAviso('Bootstrap token borrado');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo borrar el bootstrap token');
    } finally {
      setPending('');
    }
  };

  const reemitir = async () => {
    if (!agentId.trim() || !credencialesHabilitadas) return;
    setReissuing(true);
    setError('');
    try {
      const res = await api.reissueAgentCredential(companyId, {
        agent_id: agentId.trim(),
        reason: reissueReason.trim() || 'Rotación manual',
      });
      setSecreto({
        valor: res.agent?.api_key,
        titulo: 'Credencial reemitida',
        ayuda: 'Entrega esta clave al equipo afectado y sustituye la anterior inmediatamente.',
      });
      setAgentId('');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo reemitir la credencial');
    } finally {
      setReissuing(false);
    }
  };

  const copiar = (texto) => {
    navigator.clipboard?.writeText(texto).then(() => mostrarAviso('Copiado al portapapeles'));
  };

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1rem 0' }}>
        <div className="list-hint">Selecciona una empresa para gestionar el bootstrap y las credenciales del agente.</div>
      </div>
    );
  }

  return (
    <div className="list-page list-page--embedded">
      {/* ══ Cabecera de sección ══ */}
      <div className="list-head">
        <div className="list-head-left">
          <RocketLaunchIcon className="list-head-icon" />
          <div>
            <h2 className="list-section-title">Bootstrap y enrolamiento</h2>
            <p className="list-section-subtitle">
              Modo {policy?.rollout_mode || 'legacy'} · {tokensActivos} tokens activos
              {' · '}{activas} credenciales activas
            </p>
          </div>
        </div>
        <div className="list-head-actions">
          {aviso && <span className="list-badge list-badge--on"><CheckIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> {aviso}</span>}
          <button type="button" className="list-btn-secondary" onClick={cargar} disabled={loading}>
            <ArrowPathIcon style={{ width: 15, height: 15 }} /> Recargar
          </button>
        </div>
      </div>

      {error && <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="list-loading">
          <div className="spinner-border text-primary" aria-hidden="true" />
          <span>Cargando la configuración…</span>
        </div>
      ) : (
        <>
          {/* ══ Política ══ */}
          <Panel
            title="Política por empresa"
            hint="Decide cómo se enrolan los agentes de esta empresa, sin tocar el resto del entorno."
            actions={<span className="list-badge">{policy?.rollout_mode || 'legacy'}</span>}
          >
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label className="list-hint" style={{ flex: '2 1 280px' }}>
                Modo de despliegue
                <select
                  className="list-select"
                  style={{ width: '100%', marginTop: 4 }}
                  value={rolloutMode}
                  onChange={(e) => setRolloutMode(e.target.value)}
                >
                  {MODOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label className="list-hint" style={{ flex: '0 1 140px' }}>
                TTL del token (h)
                <input
                  className="list-input"
                  type="number"
                  min="1"
                  style={{ width: '100%', marginTop: 4 }}
                  value={expiresInHours}
                  onChange={(e) => setExpiresInHours(e.target.value)}
                />
              </label>
              <label className="list-hint" style={{ flex: '0 1 140px' }}>
                Usos por defecto
                <input
                  className="list-input"
                  type="number"
                  min="1"
                  style={{ width: '100%', marginTop: 4 }}
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                />
              </label>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <button type="button" className="list-btn-primary" onClick={guardarPolitica} disabled={savingPolicy}>
                  {savingPolicy ? 'Guardando…' : 'Guardar política'}
                </button>
              </div>
            </div>

            <div className="list-kpis list-kpis--chips" style={{ marginTop: 14, marginBottom: 0 }}>
              <span className="list-kpi-chip" title="API key estática compartida">
                <span className="list-kpi-value">{legacyHabilitado ? 'Permitida' : 'Bloqueada'}</span>
                <span className="list-kpi-label">API key legacy</span>
              </span>
              <span className="list-kpi-chip" title="Enrolamiento por token temporal">
                <span className="list-kpi-value">{bootstrapHabilitado ? 'Sí' : 'No'}</span>
                <span className="list-kpi-label">bootstrap</span>
              </span>
              <span className="list-kpi-chip" title="Credencial permanente por equipo">
                <span className="list-kpi-value">{credencialesHabilitadas ? 'Sí' : 'No'}</span>
                <span className="list-kpi-label">credencial individual</span>
              </span>
            </div>

            <Avanzar hay={policy?.rollout_mode === 'legacy'} tono="list-badge--warn">
              Esta empresa sigue en modo <strong>legacy</strong>: el backend rechazará el bootstrap y las
              credenciales individuales hasta pasar a <strong>dual</strong> o <strong>credential_only</strong>.
            </Avanzar>
            <Avanzar hay={policy?.rollout_mode === 'credential_only'} tono="list-badge--accent">
              Esta empresa ya no acepta API keys estáticas compartidas: solo bootstrap y credenciales
              individuales.
            </Avanzar>
          </Panel>

          {/* ══ Tokens de bootstrap ══ */}
          <Panel
            title="Tokens de bootstrap"
            hint="Tokens temporales para enrolar equipos nuevos sin reutilizar la API key de la empresa."
            actions={<span className="list-badge">{tokensActivos} activos de {tokens.length}</span>}
          >
            <Avanzar hay={!bootstrapHabilitado} tono="list-badge--warn">
              El bootstrap está deshabilitado por política para esta empresa: no se pueden generar tokens.
            </Avanzar>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
              <label className="list-hint" style={{ flex: '2 1 220px' }}>
                Nombre
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                />
              </label>
              <label className="list-hint" style={{ flex: '1 1 200px' }}>
                Notas (para qué lote es)
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={tokenNotes}
                  onChange={(e) => setTokenNotes(e.target.value)}
                  placeholder="Piloto Barcelona · lote portátiles"
                />
              </label>
              <button
                type="button"
                className="list-btn-primary"
                onClick={crearToken}
                disabled={creatingToken || !bootstrapHabilitado}
              >
                <PlusIcon style={{ width: 14, height: 14 }} />
                {creatingToken ? ' Generando…' : ' Generar token'}
              </button>
            </div>

            <p className="list-hint" style={{ marginTop: 0 }}>
              El token usa el TTL ({expiresInHours} h) y los usos ({maxUses}) de la política de arriba.
            </p>

            {tokens.length === 0 ? (
              <p className="list-hint" style={{ margin: 0 }}>Todavía no se ha emitido ningún token.</p>
            ) : tokens.map((token) => (
              <FilaToken
                key={token.id}
                actions={(
                  <>
                    {token.is_active ? (
                      <button
                        type="button"
                        className="list-action-btn"
                        disabled={pending === `token-revoke-${token.id}`}
                        onClick={() => setConfirmar({
                          titulo: 'Revocar bootstrap token',
                          mensaje: <>Los nuevos enrolamientos dejarán de funcionar con el token <strong>{token.name}</strong>.</>,
                          onConfirm: () => revocarToken(token.id),
                        })}
                        title="Revocar el token (deja de enrolar)"
                      >
                        <NoSymbolIcon />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="list-action-btn list-action-btn--danger"
                        disabled={pending === `token-delete-${token.id}`}
                        onClick={() => setConfirmar({
                          titulo: 'Borrar bootstrap token',
                          mensaje: <>Solo se borran tokens revocados. Borrar <strong>{token.name}</strong> es definitivo.</>,
                          onConfirm: () => borrarToken(token.id),
                        })}
                        title="Borrar el token revocado (definitivo)"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </>
                )}
              >
                <span style={{ fontWeight: 600, minWidth: 140 }}>{token.name}</span>
                <span className="list-code">{token.token_prefix}…</span>
                <span className={`list-badge ${token.is_active ? 'list-badge--on' : 'list-badge--muted'}`}>
                  {token.is_active ? 'Activo' : 'Revocado'}
                </span>
                <span className="list-hint">{token.used_count} / {token.max_uses} usos</span>
                <span className="list-hint">
                  {token.expires_at ? `Expira ${new Date(token.expires_at).toLocaleString()}` : 'Sin caducidad'}
                </span>
                {token.last_agent_id && (
                  <span className="list-hint list-code" title={token.last_agent_id}>
                    último {token.last_agent_id.slice(0, 8)}…
                  </span>
                )}
                {token.notes && <span className="list-hint">· {token.notes}</span>}
              </FilaToken>
            ))}
          </Panel>

          {/* ══ Credenciales individuales: solo la reemisión ══ */}
          <Panel
            title="Reemitir la credencial de un equipo"
            hint="Sirve para rotar la clave de un agente concreto sin tocar al resto de la empresa."
            actions={<span className="list-badge">{activas} activas de {credentials.length}</span>}
          >
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label className="list-hint" style={{ flex: '2 1 300px' }}>
                ID del agente
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4, fontFamily: 'var(--font-mono)' }}
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  placeholder="11111111-2222-3333-4444-555555555555"
                />
              </label>
              <label className="list-hint" style={{ flex: '1 1 220px' }}>
                Motivo (queda en la auditoría)
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={reissueReason}
                  onChange={(e) => setReissueReason(e.target.value)}
                  placeholder="Rotación manual"
                />
              </label>
              <button
                type="button"
                className="list-btn-primary"
                onClick={reemitir}
                disabled={reissuing || !agentId.trim() || !credencialesHabilitadas}
              >
                <KeyIcon style={{ width: 14, height: 14 }} />
                {reissuing ? ' Reemitiendo…' : ' Reemitir'}
              </button>
            </div>

            <Avanzar hay={!credencialesHabilitadas} tono="list-badge--warn">
              Las credenciales individuales están deshabilitadas por política para esta empresa.
            </Avanzar>

            <div
              style={{
                marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'var(--bg-toolbar)',
              }}
            >
              <LockClosedIcon style={{ width: 15, height: 15, color: 'var(--text-secondary)' }} />
              <span className="list-hint">
                El inventario de credenciales (prefijo, dispositivo, último uso, revocar y borrar) está en la
                pestaña <strong>Credenciales</strong>.
              </span>
              <button
                type="button"
                className="list-btn-secondary"
                style={{ padding: '3px 10px', fontSize: '0.74rem' }}
                onClick={onGoToCredentials}
                disabled={!onGoToCredentials}
              >
                Ir a Credenciales <ArrowRightIcon style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </Panel>

          {/* ══ Auditoría ══ */}
          <Panel
            title="Auditoría reciente"
            hint="Bootstrap, reemisiones, revocaciones y borrados, para soporte y seguridad."
            actions={<span className="list-badge">{events.length} eventos</span>}
          >
            {events.length === 0 ? (
              <p className="list-hint" style={{ margin: 0 }}>Sin eventos todavía.</p>
            ) : (
              <>
                {events.slice(0, 25).map((event) => {
                  const tipo = EVENTOS[event.event_type] || { label: event.event_type, tono: 'list-badge--muted' };
                  return (
                    <FilaToken key={event.id}>
                      <span className={`list-badge ${tipo.tono}`} style={{ minWidth: 150, textAlign: 'center' }}>
                        {tipo.label}
                      </span>
                      {event.agent_id && (
                        <span className="list-code" title={event.agent_id}>{event.agent_id.slice(0, 8)}…</span>
                      )}
                      <span className="list-hint">{event.reason || 'Sin motivo declarado'}</span>
                      <span className="list-hint" style={{ marginLeft: 'auto' }}>
                        {new Date(event.created_at).toLocaleString()} · {hace(event.created_at)}
                      </span>
                    </FilaToken>
                  );
                })}
                {events.length > 25 && (
                  <p className="list-hint" style={{ margin: '10px 0 0' }}>
                    Se muestran los 25 eventos más recientes de {events.length}.
                  </p>
                )}
              </>
            )}
          </Panel>
        </>
      )}

      {/* ══ Secreto recién emitido: se enseña una sola vez, así que va delante ══ */}
      {secreto?.valor && (
        <Dialog
          title={secreto.titulo}
          icon={<KeyIcon style={{ width: 18, height: 18 }} />}
          maxWidth={620}
          onClose={() => setSecreto(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => copiar(secreto.valor)}>
                <ClipboardDocumentIcon style={{ width: 14, height: 14 }} /> Copiar
              </button>
              <button type="button" className="list-btn-primary" onClick={() => setSecreto(null)}>
                Ya lo he copiado
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>{secreto.ayuda}</p>
          <pre
            style={{
              margin: 0, padding: '10px 12px', overflowX: 'auto',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--amber-600)',
              background: 'var(--bg-surface)', color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)', fontSize: '0.78rem', wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}
          >
            {secreto.valor}
          </pre>
        </Dialog>
      )}

      {/* ══ Confirmación (mismo diálogo que el resto del CMDB) ══ */}
      {confirmar && (
        <Dialog
          title={confirmar.titulo}
          onClose={() => setConfirmar(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmar(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-danger"
                onClick={async () => {
                  const accion = confirmar.onConfirm;
                  setConfirmar(null);
                  await accion();
                }}
              >
                Confirmar
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>{confirmar.mensaje}</p>
        </Dialog>
      )}
    </div>
  );
}
